import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Minus, Plus, RotateCcw, X } from 'lucide-react';

const ZOOM_MIN = 0.25;
const ZOOM_MAX = 8;
const ZOOM_STEP = 1.25;

interface MermaidFullscreenViewerProps {
  title: string;
  svg: string;
  onClose: () => void;
}

const iconButtonClass =
  'flex h-8 w-8 items-center justify-center rounded-md text-slate-600 transition-colors hover:bg-slate-200/70 dark:text-slate-300 dark:hover:bg-slate-700/70';

/** Full-screen diagram viewer: zoom (buttons, Ctrl/⌘ + wheel), drag to pan, Esc or ✕ to close. */
export const MermaidFullscreenViewer: React.FC<MermaidFullscreenViewerProps> = ({
  title,
  svg,
  onClose,
}) => {
  const [zoom, setZoom] = useState(1);
  const scrollRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ x: number; y: number; left: number; top: number } | null>(null);

  const changeZoom = useCallback((factor: number) => {
    setZoom((prev) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, prev * factor)));
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  // React registers wheel listeners as passive, so preventDefault (stop page zoom) needs a native one.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      changeZoom(event.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [changeZoom]);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el) return;
    el.setPointerCapture(event.pointerId);
    dragRef.current = { x: event.clientX, y: event.clientY, left: el.scrollLeft, top: el.scrollTop };
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    const start = dragRef.current;
    if (!el || !start) return;
    el.scrollLeft = start.left - (event.clientX - start.x);
    el.scrollTop = start.top - (event.clientY - start.y);
  };

  const endDrag = () => {
    dragRef.current = null;
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-[100] flex flex-col bg-white text-slate-800 dark:bg-slate-900 dark:text-slate-100"
    >
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-4 py-2 dark:border-slate-800 dark:bg-slate-800/60">
        <span className="truncate text-sm font-semibold">{title}</span>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => changeZoom(1 / ZOOM_STEP)} title="Zoom out" className={iconButtonClass}>
            <Minus size={16} />
          </button>
          <span className="w-12 text-center text-xs tabular-nums">{Math.round(zoom * 100)}%</span>
          <button type="button" onClick={() => changeZoom(ZOOM_STEP)} title="Zoom in" className={iconButtonClass}>
            <Plus size={16} />
          </button>
          <button type="button" onClick={() => setZoom(1)} title="Reset zoom" className={iconButtonClass}>
            <RotateCcw size={15} />
          </button>
          <button type="button" onClick={onClose} title="Close (Esc)" className={`${iconButtonClass} ml-2`}>
            <X size={18} />
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="min-h-0 flex-1 cursor-grab touch-none overflow-auto active:cursor-grabbing"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <div
          className="mx-auto p-6 [&_svg]:h-auto [&_svg]:w-full [&_svg]:!max-w-none"
          style={{ width: `${zoom * 100}%` }}
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      </div>
    </div>,
    document.body
  );
};
